import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError, of } from 'rxjs';
import { APIPromise } from 'openai';
import OpenAI from 'openai';

import {
	ResponseInputItem,
	ResponseInputText,
} from 'openai/resources/responses/responses.mjs';

import {
	AIMessage,
	Article,
	ArticleType,
	LoginInfo,
	Prompt,
	PromptRole,
	ResponseCreateInfo,
	ResponseClientRequest,
	VectorFileStatus,
} from '@meta-press/shared-plugins';
import { AIAPIResponse } from '../../model/AIAPIResponse';
import { WhisperResponse } from '../../model/WhisperResponse';
import { OpenAIService } from '../../services/openai-service';
import { ClaudeAIService } from '../../services/claude-ai-service';
import { AIModel } from '../../model/AIModel';
import {
	AIModelDefinition,
	ThinkingQuality,
} from '../../model/AIModelDefinition';
import { ModelConfig } from '../../model/ModelConfig';
import { BackgroundTaskQueueService } from '../../services/background-task-queue.service';
import { RetryConfig, DEFAULT_AI_RETRY_CONFIG } from '../../model/RetryConfig';
import { OPENAI_POLLING_STRATEGY } from '../../model/PollingStrategy';
import { TaskLifecycleHooks } from '../../model/TaskLifecycleHooks';

export enum AIServiceType {
	Anthropic,
	OpenAI,
}

/**
 * All available AI models - source of truth for model configurations.
 * Add new models here to make them available throughout the application.
 */
const AVAILABLE_MODELS: AIModelDefinition[] = [
	// OpenAI Models
	{
		id: 'gpt-4.1',
		modelName: 'gpt-4.1',
		displayName: 'GPT-4.1',
		description: 'GPT-4.1 (oletus)',
		aiService: AIServiceType.OpenAI,

		isPublic: true,
	} as AIModelDefinition,
	{
		id: 'gpt-4.1-nano',
		modelName: 'gpt-4.1-nano',
		displayName: 'GPT-4.1 Nano',
		description: 'Kevyt ja nopea GPT-4.1',
		aiService: AIServiceType.OpenAI,

		isPublic: true,
	} as AIModelDefinition,

	// GPT-5 Variants (same base model, different thinking quality)
	{
		id: 'gpt-5-minimal',
		modelName: 'gpt-5',
		displayName: 'GPT-5 (minimal)',
		description: 'Ei ajattelua',
		aiService: AIServiceType.OpenAI,
		thinkingQuality: 'none',
		isPublic: true,
	} as AIModelDefinition,
	{
		id: 'gpt-5-minimal',
		modelName: 'gpt-5',
		displayName: 'GPT-5 (minimal)',
		description: 'Vähän ajattelua',
		aiService: AIServiceType.OpenAI,
		thinkingQuality: 'minimal',
		isPublic: true,
	} as AIModelDefinition,
	{
		id: 'gpt-5-low',
		modelName: 'gpt-5',
		displayName: 'GPT-5 (low)',
		description: 'Matala ajattelu',
		aiService: AIServiceType.OpenAI,
		thinkingQuality: 'low',
		isPublic: true,
	} as AIModelDefinition,
	{
		id: 'gpt-5-medium',
		modelName: 'gpt-5',
		displayName: 'GPT-5 (medium)',
		description: 'Tasapainotettu nopeus ja ajattelu, mutta kallis',
		aiService: AIServiceType.OpenAI,
		thinkingQuality: 'medium',
		isPublic: true,
	} as AIModelDefinition,
	// {
	// 	id: 'gpt-5-high',
	// 	modelName: 'gpt-5',
	// 	displayName: 'GPT-5 (high)',
	// 	description: 'High reasoning',
	// 	aiService: AIServiceType.OpenAI,
	// 	thinkingQuality: 'high',
	// 	isPublic: true,
	// },

	// Claude Models
	// {
	// 	id: 'claude-3-5-sonnet-20241022',
	// 	modelName: 'claude-3-5-sonnet-20241022',
	// 	displayName: 'Claude 3.5 Sonnet',
	// 	description: 'Claude 3.5 Sonnet model',
	// 	aiService: AIServiceType.Anthropic,
	// 	thinkingQuality: 'none',
	// 	isPublic: false,
	// },
	// {
	// 	id: 'claude-3-7-sonnet-20250219',
	// 	modelName: 'claude-3-7-sonnet-20250219',
	// 	displayName: 'Claude 3.7 Sonnet',
	// 	description: 'Claude 3.7 Sonnet model',
	// 	aiService: AIServiceType.Anthropic,
	// 	thinkingQuality: 'none',
	// 	isPublic: false,
	// },
];

@Injectable({
	providedIn: 'root',
})
export class AiService {
	/**
	 * Get all available AI models (both public and private).
	 * @returns Array of all model definitions
	 */
	static GetAllModels(): AIModelDefinition[] {
		return AVAILABLE_MODELS;
	}

	/**
	 * Get only public models that should be shown in dropdowns.
	 * @returns Array of public model definitions
	 */
	static GetPublicModels(): AIModelDefinition[] {
		return AVAILABLE_MODELS.filter((m) => m.isPublic);
	}

	/**
	 * Find a model by its unique ID.
	 * @param id Model ID (e.g., 'gpt-5-high', 'gpt-4.1')
	 * @returns Model definition or undefined if not found
	 */
	static GetModelById(id: string): AIModelDefinition | undefined {
		return AVAILABLE_MODELS.find((m) => m.id === id);
	}

	static GetModelDefinitionById(id: string): ModelConfig | undefined {
		const modelDefinition = this.GetModelById(id);

		if (modelDefinition) {
			return this.ModelDefinitionToConfig(modelDefinition);
		}

		return undefined;
	}

	/**
	 * Find a model by its API model name.
	 * Prefers models with thinkingQuality 'none' if multiple exist.
	 * @param modelName API model name (e.g., 'gpt-5', 'gpt-4.1')
	 * @returns Model definition or undefined if not found
	 */
	static GetModelByName(modelName: string): AIModelDefinition | undefined {
		// First try to find a model with the exact name and no thinking
		const noThinkingModel = AVAILABLE_MODELS.find(
			(m) => m.modelName === modelName && m.thinkingQuality === undefined,
		);
		if (noThinkingModel) return noThinkingModel;

		// Otherwise return the first model with that name
		return AVAILABLE_MODELS.find((m) => m.modelName === modelName);
	}

	/**
	 * Legacy helper function for backward compatibility with AIModel enum.
	 * Maps old enum values to ModelConfig.
	 * @param modelName Legacy AIModel enum value
	 * @returns ModelConfig or undefined if model not found
	 * @deprecated Use GetModelById or GetModelByName instead
	 */
	static AIModel(modelName: string): ModelConfig | undefined {
		const modelDef = AiService.GetModelByName(modelName);
		if (!modelDef) return undefined;
		return AiService.ModelDefinitionToConfig(modelDef);
	}

	/**
	 * Convert an AIModelDefinition to a ModelConfig.
	 * Useful for extracting just the configuration from a full definition.
	 * @param definition Full model definition
	 * @returns ModelConfig with just the runtime configuration
	 */
	static ModelDefinitionToConfig(definition: AIModelDefinition): ModelConfig {
		const newConfig = {
			aiService: definition.aiService,
			modelName: definition.modelName,
		} as ModelConfig;

		if (definition.thinkingQuality) {
			newConfig.thinkingQuality = definition.thinkingQuality;
		}

		return newConfig;
	}

	/**
	 * Default model configuration for the service.
	 * This is used when no per-request ModelConfig is provided.
	 */
	public defaultModelConfig: ModelConfig;

	public get SelectedAIModel(): AIModel {
		// Legacy getter - convert modelName to AIModel enum
		return this.defaultModelConfig.modelName as AIModel;
	}

	public get SelectedAIService(): AIServiceType {
		return this.defaultModelConfig.aiService;
	}

	public get SelectedModelConfig(): ModelConfig {
		return this.defaultModelConfig;
	}

	constructor(
		private openAIService: OpenAIService,
		private claudeAIService: ClaudeAIService,
		private backgroundTaskQueue: BackgroundTaskQueueService,
	) {
		// Initialize with gpt-4.1 as the default model
		const defaultModel = AiService.GetModelById('gpt-4.1');
		if (!defaultModel) {
			throw new Error(
				'Default model gpt-4.1 not found in AVAILABLE_MODELS',
			);
		}
		this.defaultModelConfig =
			AiService.ModelDefinitionToConfig(defaultModel);
	}

	public GenerateImage(
		prompt: string,
	): Observable<OpenAI.Images.ImagesResponse> {
		return this.openAIService.generateImage(prompt);
	}

	public CreateResponse(
		previousMessages: AIMessage[] | undefined,
		options: {
			jsonSchema?: any;
			fileHandlesToInclude?: string[];
			vectorStoreName?: string;
			vectorStoreHandles?: string[];
			isBackgroundTask?: boolean;
			lifecycleHooks?: TaskLifecycleHooks<ResponseCreateInfo>;
			modelConfig?: ModelConfig;
			cancelCallback?: () => void;
		} = {},
	): Observable<AIAPIResponse> {
		const messages = [...(previousMessages || [])] as AIMessage[];
		const {
			fileHandlesToInclude = [],
			modelConfig = this.defaultModelConfig,
			isBackgroundTask = false,
			lifecycleHooks,
			cancelCallback,
		} = options;
		console.log(messages);

		switch (modelConfig.aiService) {
			case AIServiceType.Anthropic: {
				// TODO: Implement Anthropic routing through queue
				return this.claudeAIService
					.getPromptCompletion(
						messages,
						modelConfig.modelName as AIModel,
						modelConfig.thinkingQuality,
					)
					.pipe(
						map((response: AIAPIResponse) => {
							if (response.json === undefined) {
								console.log('Got a response from the API ');
								console.log(response);
								console.error(
									'Got an error from AI model, response is undefined. Currently doing nothing about it. Should retry?',
								);
								return response;
							} else {
								console.log('Got a response from the API ');
								console.log(response.json);
								return response;
							}
						}),
					);
			}

			case AIServiceType.OpenAI: {
				// Convert messages to OpenAI ResponseInput structure
				const responseMessages: Prompt[] = messages.map(
					(message: AIMessage) => {
						return {
							role: this.ConvertAIMessageRoleToPromptRole(
								message.role,
							),
							text: message.content,
						};
					},
				);

				const request = {
					prompts: responseMessages,
					fileHandles: fileHandlesToInclude,
					model: modelConfig.modelName,
					effort: modelConfig.thinkingQuality,
				} as ResponseClientRequest;

				// Route ALL requests through background task queue
				// Create request wrapper
				const initialRequest = () =>
					this.openAIService.CreateResponse(request);

				// Create poll wrapper - captures modelName in closure
				const pollRequest = (responseId: string) =>
					this.openAIService.GetResponse(
						responseId,
						modelConfig.modelName,
					);

				// Create response validator using ValidateAIResponse
				const responseValidator = (
					response: ResponseCreateInfo,
				): boolean => {
					// Convert to AIAPIResponse for validation
					const aiResponse =
						this.ConvertResponseCreateInfoToAIAPIResponse(response);
					return this.ValidateAIResponse(aiResponse);
				};

				// Add task to queue with retry config, validator, polling strategy, and lifecycle hooks
				const { taskId, observable } = this.backgroundTaskQueue.addTask(
					initialRequest,
					pollRequest,
					DEFAULT_AI_RETRY_CONFIG,
					responseValidator,
					OPENAI_POLLING_STRATEGY,
					lifecycleHooks,
				);

				// Convert ResponseCreateInfo to AIAPIResponse
				return observable.pipe(
					map((response: ResponseCreateInfo) => {
						return this.ConvertResponseCreateInfoToAIAPIResponse(
							response,
						);
					}),
				);
			}

			default:
				return throwError(() => new Error('Unsupported AI service'));
		}
	}

	ConvertAIMessageRoleToPromptRole(role: string): PromptRole {
		switch (role) {
			case 'system':
				return PromptRole.System;
			case 'assistant':
				return PromptRole.Assistant;
			case 'user':
				return PromptRole.User;
		}

		return PromptRole.User;
	}

	ConvertResponseCreateInfoToAIAPIResponse(
		response: ResponseCreateInfo,
	): AIAPIResponse {
		// Handle null output (occurs when response is still queued/in-progress)
		if (!response.output) {
			return {
				rawResponse: '',
				json: undefined,
				references: undefined,
				error: response.error,
				jsonParseError: false,
			};
		}

		let jsonString = response.output;
		// Parse possible json from response
		const jsonIndex = jsonString.indexOf('```json');

		if (jsonIndex > -1) {
			// There might be some response text before the first ```json. Remove everything before it, including the ```json
			jsonString = jsonString.substring(jsonIndex + '```json'.length);

			// Find the index of the ending ```
			const jsonEndIndex = jsonString.indexOf('```');

			if (jsonEndIndex > -1) {
				// Remove the ending ```
				jsonString = jsonString.substring(0, jsonEndIndex);
			}
		}

		let parsedJson = {};
		let jsonParseError = false;
		try {
			if (jsonString.trim()) {
				parsedJson = JSON.parse(jsonString);
			}
		} catch (e) {
			jsonParseError = true;
			console.error('Error parsing JSON', e);
			console.log('JSON String: ', jsonString);
		}

		const aiAPIResponse = {
			rawResponse: response.output,
			json: parsedJson,
			jsonParseError: jsonParseError,
			error: response.error,
		} as AIAPIResponse;

		return aiAPIResponse;
	}

	/**
	 * Set the default model configuration.
	 * @param config Model configuration to use as default
	 */
	public SetModelConfig(config: ModelConfig): void {
		this.defaultModelConfig = config;
	}

	/**
	 * Set the default model by its ID.
	 * @param modelId Model ID (e.g., 'gpt-5-high', 'gpt-4.1')
	 */
	public SetModelById(modelId: string): void {
		const model = AiService.GetModelById(modelId);
		if (!model) {
			throw new Error(`Model with ID '${modelId}' not found`);
		}
		this.defaultModelConfig = AiService.ModelDefinitionToConfig(model);
	}

	public InitializeClientAPI(apiKey: string): void {
		switch (this.defaultModelConfig.aiService) {
			case AIServiceType.Anthropic: {
				throw new Error('Not implemented.');
				break;
			}
			case AIServiceType.OpenAI: {
				this.openAIService.InitializeClient(apiKey);
				break;
			}
		}
	}

	public DeInitializeClientAPI(): void {
		switch (this.defaultModelConfig.aiService) {
			case AIServiceType.Anthropic: {
				// No cleanup needed for Anthropic (not implemented)
				break;
			}
			case AIServiceType.OpenAI: {
				this.openAIService.DeInitializeClient();
				break;
			}
		}
	}

	public CancelResponse(
		responseId: string,
		modelName: string,
	): Observable<boolean> {
		switch (this.defaultModelConfig.aiService) {
			case AIServiceType.Anthropic: {
				return throwError(
					() =>
						new Error(
							'Anthropic does not support response cancellation',
						),
				) as Observable<boolean>;
			}
			case AIServiceType.OpenAI: {
				return this.openAIService
					.CancelResponse(responseId, modelName)
					.pipe(
						map(() => true),
						catchError((error) => {
							console.error('Error cancelling response:', error);
							return throwError(() => error);
						}),
					);
			}
		}
	}

	// Extract concepts from an article text for use in card sorting game	// Extract concepts from an article text for use in card sorting game
	public ExtractConcepts(
		articleText: string,
		maxConcepts = 20,
	): Promise<{
		concepts: Array<{
			term: string;
			definition: string;
			importance: number;
		}>;
	}> {
		const systemPrompt = `You are an expert educational content creator that helps extract key concepts, terms, and definitions from text.
Extract the ${maxConcepts} most important concepts from the provided text.
For each concept, provide:
1. The term or concept name
2. A short definition or explanation (1-2 sentences)
3. The importance of this concept to understanding the overall text (on a scale of 1-5)

Format your response as a JSON object with the following structure:
{
  "concepts": [
    {
      "term": "concept name",
      "definition": "short definition",
      "importance": 5
    }
  ]
}`;

		const userPrompt = `Please extract key concepts from this text:\n\n${articleText}`;

		return new Promise<{
			concepts: Array<{
				term: string;
				definition: string;
				importance: number;
			}>;
		}>((resolve, reject) => {
			const messages = [
				{ role: 'system', content: systemPrompt },
				{ role: 'user', content: userPrompt },
			] as AIMessage[];

			// Create a JSON schema for the expected response format
			const jsonSchema = {
				type: 'object',
				properties: {
					concepts: {
						type: 'array',
						items: {
							type: 'object',
							properties: {
								term: { type: 'string' },
								definition: { type: 'string' },
								importance: { type: 'number' },
							},
							required: ['term', 'definition'],
						},
					},
				},
			};

			// Use the existing GetPrompt method which works with both services
			this.CreateResponse(messages, { jsonSchema }).subscribe({
				next: (response: AIAPIResponse) => {
					if (response.json) {
						resolve(response.json);
					} else {
						reject(new Error('Failed to parse response as JSON'));
					}
				},
				error: (err) => {
					console.error('Error extracting concepts:', err);
					reject(err);
				},
			});
		});
	}

	public GetStoreFilesStatus(
		fileIds: number[],
	): Observable<VectorFileStatus[]> {
		switch (this.defaultModelConfig.aiService) {
			case AIServiceType.OpenAI: {
				return this.openAIService.GetStoreFilesStatus(fileIds);
			}

			default: {
				throw new Error('Not implemented.');
			}
		}
	}

	public ClearVectorStore(): Observable<any> {
		switch (this.defaultModelConfig.aiService) {
			case AIServiceType.OpenAI: {
				return this.openAIService.ClearVectorStore();
			}

			default: {
				throw new Error('Not implemented.');
			}
		}
	}

	public CheckAndUploadToOpenai(fileIds: number[]): Observable<any> {
		switch (this.defaultModelConfig.aiService) {
			case AIServiceType.OpenAI: {
				return this.openAIService.CheckAndUploadToOpenai(fileIds);
			}

			default: {
				throw new Error('Not implemented.');
			}
		}
	}

	/**
	 * Validate if the AI response is valid and usable
	 * Returns true if response is valid, false if validation fails (needs retry)
	 * Only checks for generic issues, not domain-specific structures
	 */
	public ValidateAIResponse(aiResponse: AIAPIResponse): boolean {
		// Check if JSON parsing failed (empty object) and response appears to be plain text
		const hasEmptyJson =
			!aiResponse.json || Object.keys(aiResponse.json).length === 0;
		const rawResponse = aiResponse.rawResponse;

		console.log(
			`[VALIDATION CHECK] hasEmptyJson: ${hasEmptyJson}, jsonParseError: ${aiResponse.jsonParseError}, rawResponse type: ${typeof rawResponse}, length: ${rawResponse?.length}`,
		);

		// Case 1: No raw response or undefined/null - invalid response
		if (
			!rawResponse ||
			rawResponse === 'undefined' ||
			rawResponse === 'null'
		) {
			console.log(
				'Detected missing/null/undefined raw response, validation failed',
			);
			return false;
		}

		// Case 2: JSON parsing failed with an error - invalid response
		if (aiResponse.jsonParseError) {
			console.log(
				'Detected JSON parse error (malformed JSON), validation failed',
			);
			return false;
		}

		// Case 3: Completely empty JSON - check various scenarios
		if (hasEmptyJson) {
			const trimmedResponse = rawResponse.trim();

			// If it's literally just "{}" or "[]", invalid (AI returned empty structure)
			if (trimmedResponse === '{}' || trimmedResponse === '[]') {
				console.log(
					'Detected empty JSON object/array, validation failed',
				);
				return false;
			}

			// If it's completely empty string, invalid
			if (trimmedResponse === '') {
				console.log(
					'Detected empty string response, validation failed',
				);
				return false;
			}

			// Check if response looks like plain text rather than JSON structure
			const hasJsonStructure =
				trimmedResponse.includes('{') && trimmedResponse.includes('}');
			const hasJsonCodeBlock = trimmedResponse.includes('```json');

			// If it has no JSON structure and no JSON code block, it's likely plain text - invalid
			if (!hasJsonStructure && !hasJsonCodeBlock) {
				console.log(
					'Detected text-only response without JSON structure:',
					trimmedResponse.substring(0, 200) + '...',
				);
				return false;
			}
		}

		console.log('[VALIDATION CHECK] Response is valid');
		return true;
	}
}
